/**
 * EntryModelLinkingContent Component
 *
 * Main content component for the Entry Models tab in PlaybookLinkageTabs.
 * Displays linked entry models for the selected playbook with unlink actions,
 * provides a picker modal to add entry models, and supports inline editing
 * of entry model items via the EntryModelModal.
 */

import React, { useState, useCallback, useEffect } from 'react';

import { LinkedEntryModelsList } from './LinkedEntryModelsList';
import { EntryModelLinkPickerModal } from './EntryModelLinkPickerModal';
import { EntryModelModal } from '../EntryModels/EntryModelModal';
import { useStrategiesPlaybooks } from '../context/StrategiesPlaybooksContext';
import { entryModelService } from '@/services/EntryModelService';
import { entryModelCategoryService } from '@/services/EntryModelCategoryService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import type { EntryModel } from '@/types/schema/EntryModel';
import type { EntryModelCategory } from '@/types/schema/EntryModelCategory';

export type EntryModelLinkingContentProps = Record<string, never>;

export const EntryModelLinkingContent: React.FC<EntryModelLinkingContentProps> = () => {
  const { localPlaybookEntryModelIds, setLocalPlaybookEntryModelIds } = useStrategiesPlaybooks();

  // Picker modal state
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  // Edit modal state
  const [editingEntryModel, setEditingEntryModel] = useState<EntryModel | undefined>(undefined);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Create modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Shared saving state for both edit and create modals
  const [isSaving, setIsSaving] = useState(false);

  // Categories for edit modal
  const [categories, setCategories] = useState<EntryModelCategory[]>([]);
  const [allCategories, setAllCategories] = useState<EntryModelCategory[]>([]);

  /**
   * Load categories for the edit modal
   * Called on mount and before opening edit modal to ensure fresh data
   */
  const loadCategories = useCallback(async () => {
    try {
      const allCats = await entryModelCategoryService.getAll();
      setAllCategories(allCats);
      setCategories(allCats.filter((c) => c.status === 'active'));
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  }, []);

  // Load categories on mount
  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  /**
   * Handle unlinking a single entry model item
   * Removes the ID from localPlaybookEntryModelIds, marking the playbook dirty
   * Uses functional update to handle rapid sequential unlinks correctly
   * No min count enforcement — empty array is valid (soft requirement)
   */
  const handleUnlink = useCallback(
    (entryModelId: string) => {
      setLocalPlaybookEntryModelIds((prev) => prev.filter((id) => id !== entryModelId));
    },
    [setLocalPlaybookEntryModelIds]
  );

  /**
   * Handle selection change from the picker modal
   * Replaces localPlaybookEntryModelIds with the new selection
   */
  const handleSelectionChange = useCallback(
    (selectedIds: string[]) => {
      setLocalPlaybookEntryModelIds(selectedIds);
    },
    [setLocalPlaybookEntryModelIds]
  );

  /**
   * Handle edit action from the linked list
   * Refreshes categories and opens the edit modal with the selected entry model
   */
  const handleEdit = useCallback(
    async (entryModel: EntryModel) => {
      // Refresh categories before opening modal to ensure fresh data
      await loadCategories();
      setEditingEntryModel(entryModel);
      setIsEditModalOpen(true);
    },
    [loadCategories]
  );

  /**
   * Handle closing the edit modal
   * Clears editingEntryModel state
   */
  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setEditingEntryModel(undefined);
  }, []);

  /**
   * Handle save from the edit modal
   * Updates the entry model via service and notifies sync
   * No manual data reload needed - LinkedEntryModelsList's sync subscription handles refresh
   */
  const handleSave = useCallback(
    async (data: { name: string; description?: string; entryModelCategoryId?: string }) => {
      if (!editingEntryModel) return;

      setIsSaving(true);
      try {
        await entryModelService.update(editingEntryModel.id, data);
        syncCoordinatorService.notifyLocalChange('entry_models', editingEntryModel.id);
        syncCoordinatorService.notifyChange('entry_models', editingEntryModel.id);
        handleCloseEditModal();
      } catch (error) {
        console.error('Failed to save entry model:', error);
      } finally {
        setIsSaving(false);
      }
    },
    [editingEntryModel, handleCloseEditModal]
  );

  /**
   * Handle opening the create modal
   * Clears any stale editing state, loads fresh categories, then opens modal
   */
  const handleOpenCreateModal = useCallback(async () => {
    // Clear any stale editing state to prevent data flash
    setEditingEntryModel(undefined);
    // Load fresh categories before opening modal
    await loadCategories();
    setIsCreateModalOpen(true);
  }, [loadCategories]);

  /**
   * Handle closing the create modal
   */
  const handleCloseCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
  }, []);

  /**
   * Handle create from the create modal
   * Creates new entry model via service, notifies sync, auto-links to playbook, closes modal
   */
  const handleCreate = useCallback(
    async (data: { name: string; description?: string; entryModelCategoryId?: string }) => {
      setIsSaving(true);
      try {
        // Build entry model data, only including optional fields if they have values
        // (required for exactOptionalPropertyTypes)
        const entryModelData: { name: string; description?: string; entryModelCategoryId?: string } = {
          name: data.name,
        };
        if (data.description) {
          entryModelData.description = data.description;
        }
        if (data.entryModelCategoryId) {
          entryModelData.entryModelCategoryId = data.entryModelCategoryId;
        }

        // Create the new entry model entity
        const newEntryModel = await entryModelService.add(entryModelData);

        // Notify sync for same-tab and cross-tab updates
        syncCoordinatorService.notifyLocalChange('entry_models', newEntryModel.id);
        syncCoordinatorService.notifyChange('entry_models', newEntryModel.id);

        // Auto-link the new entry model to the current playbook
        setLocalPlaybookEntryModelIds((prev) => [...prev, newEntryModel.id]);

        handleCloseCreateModal();
      } catch (error) {
        console.error('Failed to create entry model:', error);
      } finally {
        setIsSaving(false);
      }
    },
    [setLocalPlaybookEntryModelIds, handleCloseCreateModal]
  );

  // Determine which modal is open and configure props accordingly
  const isModalOpen = isEditModalOpen || isCreateModalOpen;
  const modalEntryModel = isCreateModalOpen ? undefined : editingEntryModel;
  const handleModalClose = isCreateModalOpen ? handleCloseCreateModal : handleCloseEditModal;
  const handleModalSave = isCreateModalOpen ? handleCreate : handleSave;

  return (
    <>
      {/* Linked Entry Models List with unlink, add existing, create new, and edit actions */}
      <LinkedEntryModelsList
        entryModelIds={localPlaybookEntryModelIds}
        onUnlink={handleUnlink}
        onAdd={() => setIsPickerOpen(true)}
        onCreateNew={handleOpenCreateModal}
        onEdit={handleEdit}
      />

      {/* Entry Model Picker Modal */}
      <EntryModelLinkPickerModal
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        selectedEntryModelIds={localPlaybookEntryModelIds}
        onSelectionChange={handleSelectionChange}
      />

      {/* Entry Model Modal - reused for both edit and create modes */}
      <EntryModelModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        entryModel={modalEntryModel}
        categories={categories}
        allCategories={allCategories}
        onSave={handleModalSave}
        isSaving={isSaving}
      />
    </>
  );
};
